import React from 'react';
import PropTypes from 'prop-types';
import { FormControl } from './Controls/FormControl';
import { CheckboxGroupControl } from './Controls/CheckboxGroupControl';
import { RadioGroupControl } from './Controls/RadioGroupControl';
import FormGroup from 'react-bootstrap/FormGroup';
import FormLabel from 'react-bootstrap/FormLabel';
import { InfoTooltip } from '../InfoTooltip';
import { UnderscoresToTitleCase } from '../../utils/StringUtil';
import { GetValidationPattern, GetValidationMessage } from '../../utils/ValidationUtil';
import { i18n } from '../../i18n/translate';
import { kebabCase } from 'lodash';
import { formatClassName } from '../../utils';
import { Icon, Typography } from '../../core';
import { FormLabel as ChakraFormLabel } from '@chakra-ui/react';

export class FormField extends React.Component {
  static propTypes = {
    activeLanguage: PropTypes.string,
    caption: PropTypes.string,
    children: PropTypes.element,
    className: PropTypes.string,
    data: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      type: PropTypes.string,
    }),
    dataObjectAction: PropTypes.string,
    dataObjectAttribute: PropTypes.string,
    dataObjectId: PropTypes.string,
    dataObjectType: PropTypes.string,
    dataValue: PropTypes.any,
    disabled: PropTypes.bool,
    errorColor: PropTypes.string,
    errorMessage: PropTypes.string,
    hintLink: PropTypes.node,
    hintText: PropTypes.string,
    hideControlLabelAria: PropTypes.bool,
    inputClassName: PropTypes.string,
    labelClassName: PropTypes.string,
    legendHeadingLevel: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
    isFloatOutside: PropTypes.bool,
    languages: PropTypes.arrayOf(PropTypes.object),
    multilingual: PropTypes.bool,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onValueChange: PropTypes.func,
    pattern: PropTypes.string,
    required: PropTypes.bool,
    runValidation: PropTypes.bool,
    scrollContainerId: PropTypes.string,
    searchField: PropTypes.element,
    srHelpText: PropTypes.string,
    tooltip: PropTypes.string,
    tooltipPosition: PropTypes.string,
    validationType: PropTypes.string,
    valueChangeDelay: PropTypes.number,
    wrapperClassName: PropTypes.string,
  };

  static defaultProps = {
    errorColor: '#e71c0d',
    tooltipPosition: 'left',
    legendHeadingLevel: 'h3',
  };

  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      validationError: false,
    };
  }

  setError = (error, message) => {
    if (this.state.hasError !== error) {
      this.setState({ hasError: error }, () => {
        if (this.props.validationType && this.state.hasError) {
          this.setState({ validationError: GetValidationMessage(this.props.validationType) });
        } else if (this.state.hasError && message) {
          this.setState({ validationError: message });
        } else {
          this.setState({ validationError: false });
        }
      });
    } else if (this.state.validationError !== message && !this.props.validationType) {
      this.setState({ validationError: message });
    }
  };

  componentDidUpdate() {
    if (this.props.runValidation && this.control && this.control.isValid) {
      this.control.checkError();
    }

    if (this.control?.props?.required && this.control?.props?.files) {
      const fieldLabel = this.props.caption || this.props.name;
      const requiredMessage = fieldLabel
        ? i18n.t('%s is required', { postProcess: 'sprintf', sprintf: [fieldLabel] })
        : i18n.t('This field is required');
      this.control.activeFiles().length > 0 ? this.setError(false) : this.setError(true, requiredMessage);
    }
  }

  renderField = () => {
    const pattern = this.props.validationType ? GetValidationPattern(this.props.validationType) : this.props.pattern;
    const hasError = this.state.hasError || !!this.props.errorMessage;

    // Merge the auto-generated hint ID with any custom aria-describedby passed in, only when hint is rendered
    const hasHint = !!(this.props.hintText || this.props.errorMessage || this.state.validationError);
    const hintDescribedBy = hasHint ? `${this.props.name}-hint` : undefined;
    const customDescribedBy = this.props['aria-describedby'];
    const mergedDescribedBy = [hintDescribedBy, customDescribedBy].filter(Boolean).join(' ') || undefined;

    if (this.props.children) {
      return React.cloneElement(React.Children.only(this.props.children), {
        dataValue: this.props.dataValue,
        setError: this.setError,
        required: this.props.required,
        name: this.props.name,
        isFloatOutside: this.props.isFloatOutside,
        onValueChange: this.props.onValueChange,
        valueChangeDelay: this.props.valueChangeDelay,
        ref: control => (this.control = control),
        onBlur: this.props.onBlur,
        onFocus: this.props.onFocus,
        scrollContainerId: this.props.scrollContainerId,
        disabled: this.props.disabled,
        caption: this.props.caption,
        'aria-describedby': mergedDescribedBy,
        'aria-invalid': hasError ? 'true' : undefined,
      });
    } else {
      return (
        <FormControl
          {...this.props}
          pattern={pattern ?? this.props.pattern}
          ref={control => (this.control = control)}
          setError={this.setError}
          className={this.props.inputClassName}
          aria-describedby={mergedDescribedBy}
          aria-invalid={hasError ? 'true' : undefined}
        />
      );
    }
  };

  renderLowerText() {
    const { hintText, hintLink, errorMessage } = this.props;
    const { validationError } = this.state;
    let lowerText = hintText || '';
    let lowerTextStyle = 'input-hint';

    const error = errorMessage || validationError;
    if (error) {
      lowerText = error;
      lowerTextStyle = 'input-error';
    }

    if (!lowerText) return null;

    const commonHintProps = {
      className: lowerTextStyle,
      'data-action': lowerTextStyle,
      id: `${this.props.name}-hint`,
      ...(lowerTextStyle === 'input-error' && { role: 'alert' }),
    };

    return (
      <>
        {lowerTextStyle === 'input-error' && <Icon iconName="exclamation-circle" colorScheme="error" />}
        {typeof lowerText === 'string' ? (
          <span {...commonHintProps} dangerouslySetInnerHTML={{ __html: UnderscoresToTitleCase(lowerText) }} />
        ) : (
          <span {...commonHintProps}>{lowerText}</span>
        )}
        {hintLink && !validationError ? hintLink : null}
      </>
    );
  }

  render() {
    const {
      activeLanguage,
      className: propClassName,
      data,
      dataObjectId,
      dataObjectType,
      dataObjectAttribute,
      dataObjectAction,
      errorMessage,
      isFloatOutside,
      languages,
      multilingual,
      name,
      required,
      srHelpText,
    } = this.props;

    const currentLanguage = languages?.filter(language => Object.keys(language)[0] === activeLanguage) || '';
    const className = formatClassName([
      propClassName,
      { 'has-error is-invalid': this.state.hasError || errorMessage },
      { 'required-form-control': required },
      { 'fg-floatoutside': isFloatOutside },
    ]);

    const caption = multilingual ? `${this.props.caption} (${currentLanguage[0][activeLanguage]})` : this.props.caption;

    const dataId = dataObjectId || (data && data.id && data.id);
    const dataType = dataObjectType || (data && data.type && data.type);
    const dataAttribute = dataObjectAttribute || (data && data.name && data.name);

    React.Children.forEach(this.props.children, child => {
      if (child.type.propTypes?.controlLabelText && caption) {
        console.warn(`
          This component has its own caption capability (controlLabeltext).
          Please move the caption from the FromField wrapper to the wrapped
          component.
        `);
      }
    });

    // Detect checkbox/radio group children that need fieldset/legend instead of label
    const isGroupControl =
      this.props.children &&
      (this.props.children.type === CheckboxGroupControl ||
        this.props.children.type === RadioGroupControl ||
        this.props.children.type?.displayName === 'CheckboxGroupControl' ||
        this.props.children.type?.displayName === 'RadioGroupControl');

    return (
      <FormGroup
        controlId={name}
        className={className}
        data-field-name={name}
        data-object-id={dataId}
        data-object-type={dataType}
        data-object-attribute={dataAttribute || `${kebabCase(name)}-form-field`}
        data-object-action={dataObjectAction}
        key={name}
      >
        <div className={this.props.wrapperClassName}>
          {isGroupControl ? (
            // Checkbox/radio groups use fieldset/legend for proper screen reader grouping
            <fieldset>
              {caption && (
                <ChakraFormLabel as="legend" id={`${name}-label`}>
                  <Typography variant={this.props.legendHeadingLevel || 'h3'}>
                    {caption}
                    {required && <span aria-hidden="true">*</span>}
                  </Typography>
                </ChakraFormLabel>
              )}
              {this.renderField()}
            </fieldset>
          ) : (
            <>
              {caption && (
                <FormLabel
                  className={this.props.labelClassName}
                  aria-hidden={this.props.hideControlLabelAria}
                  id={`${this.props.name}-label`}
                >
                  {this.props.searchField || ''} {caption}
                  {required && <span aria-hidden="true">*</span>}{' '}
                  {srHelpText && <span className="sr-only">{srHelpText}</span>}
                </FormLabel>
              )}
              {this.renderField()}
            </>
          )}
          {this.props.tooltip ? (
            <InfoTooltip
              {...this.props}
              label={i18n.t('More information about %s', {
                postProcess: 'sprintf',
                sprintf: [this.props.caption],
              })}
            />
          ) : null}
        </div>
        {this.renderLowerText()}
      </FormGroup>
    );
  }
}
